/**
 * WebGLContext - Owns the WebGL2 context and canvas management
 *
 * Provides the GL context to renderers and shaders, handles canvas resize
 * with DPR support, and manages global GL state.
 */

export class WebGLContext {
  readonly canvas: HTMLCanvasElement;
  readonly gl: WebGL2RenderingContext;

  // Canvas dimensions in CSS pixels
  private cssWidth = 0;
  private cssHeight = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    });

    if (!gl) {
      throw new Error('WebGL2 not supported');
    }

    this.gl = gl;

    // Initial setup
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    this.setupCanvas();
  }

  /**
   * Resize the canvas to fit its parent element.
   */
  resize(): void {
    this.setupCanvas();
  }

  /**
   * Set explicit canvas dimensions (for headless rendering).
   */
  setDimensions(width: number, height: number): void {
    this.cssWidth = width;
    this.cssHeight = height;
    this.canvas.width = width;
    this.canvas.height = height;
    this.gl.viewport(0, 0, width, height);
  }

  /**
   * Get current canvas dimensions in CSS pixels.
   */
  getDimensions(): { width: number; height: number } {
    return { width: this.cssWidth, height: this.cssHeight };
  }

  /**
   * Get canvas dimensions in physical pixels.
   */
  getPhysicalDimensions(): { width: number; height: number } {
    return { width: this.canvas.width, height: this.canvas.height };
  }

  /**
   * Get device pixel ratio.
   */
  getDevicePixelRatio(): number {
    return window.devicePixelRatio || 1;
  }

  /**
   * Clear the current framebuffer with the specified color.
   */
  clear(r: number = 0.1, g: number = 0.1, b: number = 0.1, a: number = 1.0): void {
    const gl = this.gl;
    gl.clearColor(r, g, b, a);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  /**
   * Bind the default framebuffer (canvas).
   */
  bindDefaultFramebuffer(): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  private setupCanvas(): void {
    const rect = this.canvas.parentElement?.getBoundingClientRect();
    if (rect) {
      this.cssWidth = rect.width;
      this.cssHeight = rect.height;

      const dpr = window.devicePixelRatio || 1;
      this.canvas.width = rect.width * dpr;
      this.canvas.height = rect.height * dpr;
      this.canvas.style.width = `${rect.width}px`;
      this.canvas.style.height = `${rect.height}px`;
    }
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }
}
